"use client";

import { useMemo, useState } from 'react'
import { navigateInShell, shellLinkClick } from '@/lib/clientNav'
import { useFinance, useMonthlyAgg, useTagPolicy, latestMonth } from '@/lib/finance/finance-data-context'
import { balanceAsOf, contributionAsOf } from '@/lib/finance/asof'
import { addMonths, currentMonthKey, fmtDate, fmtDateTime, fmtMonth, fmtUsd, fmtUsdCompact } from '@/lib/finance/format'
import { accountGroupOf, accountGroupColor, accountGroupId, ACCOUNT_GROUPS } from '@/lib/finance/categories'
import { Card, PageHeader, StatTile, LegendKey, Money, Segmented, TagBadges, TxnListItem, useToggleSet } from '@/components/finance/ui'
import { usePageRange, rangeLabel } from '@/components/finance/RangePicker'
import { NetWorthChart, NetWorthOverlayPills, useNetWorthRows } from '@/components/finance/NetWorthHeroChart'
import { SpendingDonut } from '@/components/finance/SpendingDonut'

export function Overview() {
  const { networth, accounts, txns, meta } = useFinance()
  const monthly = useMonthlyAgg()
  const { policy: tagPolicy } = useTagPolicy()

  const months = networth.months
  const last = latestMonth(months)
  const assets = networth.series.assets ?? {}
  const debts = networth.series.debts ?? {}
  const cash = networth.series.cash ?? {}
  const investments = networth.series.investments ?? {}
  const fire = networth.series.fireNetWorth ?? {}

  // Hero chart defaults to the FIRE series; every overlay (net worth included)
  // is toggleable.
  const [overlays, toggleOverlay] = useToggleSet<string>(['fire'])

  const { startMonth, endMonth, state: rangeState, control } = usePageRange(months[0], last)
  const nwSeries = useNetWorthRows(networth, startMonth, endMonth)
  // All page comparisons reflect the selected range, not just the last month.
  const rangeStart = nwSeries[0]?.month ?? months[months.length - 2]
  const deltaLabel = rangeLabel(rangeState)

  // The spending card is anchored to a calendar month (current or previous,
  // via the toggle) — if no transactions have been imported for it yet, it
  // shows an empty state rather than silently jumping back to older data.
  const nowMonth = currentMonthKey()
  const [spendWhich, setSpendWhich] = useState<'current' | 'previous'>('current')
  const spendMonth = spendWhich === 'current' ? nowMonth : addMonths(nowMonth, -1)
  const spendAgg = monthly.find((m) => m.month === spendMonth) ?? null
  const lastTxnMonth = monthly.length ? monthly[monthly.length - 1].month : null

  const recent = txns?.slice(0, 8) ?? null

  // Account group subtotals at the latest balance month, plus the change over
  // the selected range (start-of-window → latest).
  const groupTotals = useMemo(() => {
    const now = new Map<string, number>()
    const then = new Map<string, number>()
    for (const a of accounts) {
      const g = accountGroupOf(a.type)
      // As-of sums, matching net worth: a not-updated-today account still counts
      // (its latest value), a closed account drops out after its last snapshot.
      if (balanceAsOf(a, last) !== undefined) now.set(g, (now.get(g) ?? 0) + contributionAsOf(a, last))
      if (balanceAsOf(a, rangeStart) !== undefined) then.set(g, (then.get(g) ?? 0) + contributionAsOf(a, rangeStart))
    }
    return ACCOUNT_GROUPS.filter((g) => now.has(g)).map((g) => ({
      group: g,
      total: now.get(g)!,
      delta: now.get(g)! - (then.get(g) ?? 0),
    }))
  }, [accounts, last, rangeStart])

  return (
    <div className="space-y-4">
      <PageHeader title="Overview">{control}</PageHeader>

      {/* Hero: current net worth */}
      <Card>
        {/* Overlay toggles — top-right; net worth always shows (fixed pill) */}
        <div className="mb-3 flex flex-wrap items-center justify-end gap-1.5">
          <NetWorthOverlayPills overlays={overlays} onToggle={toggleOverlay} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs text-ink-2">FIRE net worth · {fmtDate(last)}</div>
            <div className="mt-1 text-5xl font-semibold tracking-tight">{fmtUsd(fire[last] ?? 0)}</div>
            <div className="mt-1 text-sm">
              <Money value={(fire[last] ?? 0) - (fire[rangeStart] ?? 0)} compact />{' '}
              <span className="text-ink-3">since {fmtDate(rangeStart)}</span>
            </div>
          </div>
          <div className="h-56 min-w-0 flex-1 basis-72">
            <NetWorthChart rows={nwSeries} overlays={overlays} startMonth={startMonth} endMonth={endMonth} minTickGap={40} />
          </div>
        </div>
      </Card>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Assets"
          value={fmtUsdCompact(assets[last] ?? 0)}
          delta={(assets[last] ?? 0) - (assets[rangeStart] ?? 0)}
          deltaLabel={deltaLabel}
        />
        <StatTile
          label="Debts"
          value={fmtUsdCompact(debts[last] ?? 0)}
          delta={(debts[last] ?? 0) - (debts[rangeStart] ?? 0)}
          deltaLabel={deltaLabel}
        />
        <StatTile
          label="Cash + equivalents"
          value={fmtUsdCompact(cash[last] ?? 0)}
          delta={(cash[last] ?? 0) - (cash[rangeStart] ?? 0)}
          deltaLabel={deltaLabel}
        />
        <StatTile
          label="Investments"
          value={fmtUsdCompact(investments[last] ?? 0)}
          delta={(investments[last] ?? 0) - (investments[rangeStart] ?? 0)}
          deltaLabel={deltaLabel}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Spending snapshot — current or previous calendar month */}
        <Card
          title={`Spending · ${fmtMonth(spendMonth)}`}
          subtitle={spendAgg ? `${fmtUsd(spendAgg.spending)} spent · ${fmtUsd(spendAgg.income)} income` : undefined}
          actions={
            <a
 onClick={shellLinkClick}
 href="/finance/trends" className="text-xs font-medium text-accent-ink hover:underline">
              See trends →
            </a>
          }
        >
          <div className="mb-3">
            <Segmented
              ariaLabel="Spending month"
              options={[
                { value: 'current', label: 'This month' },
                { value: 'previous', label: 'Last month' },
              ]}
              value={spendWhich}
              onChange={setSpendWhich}
            />
          </div>
          {spendAgg ? (
            <SpendingDonut
              byGroup={spendAgg.byGroup}
              onSelect={(group) =>
                navigateInShell(
                  `/finance/trends?view=category&group=${encodeURIComponent(group)}&from=${spendMonth}&to=${spendMonth}`,
                )
              }
            />
          ) : (
            <div className="py-6 text-center text-sm text-ink-3">
              <p>No transactions recorded for {fmtMonth(spendMonth)} yet.</p>
              {lastTxnMonth && (
                <p className="mt-1 text-xs">
                  Transaction history runs through {fmtMonth(lastTxnMonth)} — run a
                  gather + sync to catch up.
                </p>
              )}
            </div>
          )}
        </Card>

        {/* Accounts summary */}
        <Card
          title="Accounts"
          subtitle={`Balances as of ${fmtDateTime(meta.balancesAsOf)}`}
          actions={
            <a
 onClick={shellLinkClick}
 href="/finance/accounts" className="text-xs font-medium text-accent-ink hover:underline">
              All accounts →
            </a>
          }
        >
          <ul className="divide-y divide-edge">
            {groupTotals.map(({ group, total, delta }) => (
              <li key={group}>
                <a
                  onClick={shellLinkClick}
                  href={`/finance/accounts#${accountGroupId(group)}`}
                  className="-mx-2 flex items-center justify-between gap-2 rounded-lg px-2 py-2 hover:bg-surface-3/50"
                >
                  <LegendKey color={accountGroupColor(group)} label={group} />
                  <span className="flex shrink-0 items-baseline gap-2.5">
                    <span className="w-28 text-right text-sm font-medium tabular-nums">{fmtUsd(total)}</span>
                    <span className="w-16 text-right text-xs">
                      {Math.abs(delta) >= 0.5 ? <Money value={delta} compact /> : null}
                    </span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Recent transactions */}
      <Card
        title="Recent transactions"
        actions={
          <a
 onClick={shellLinkClick}
 href="/finance/transactions" className="text-xs font-medium text-accent-ink hover:underline">
            All transactions →
          </a>
        }
      >
        {recent === null && (
          <p className="py-4 text-center text-sm text-ink-3">Loading transactions…</p>
        )}
        {/* Same detail the full Transactions page shows — tags and the
            account — so the glanceable list doesn't hide what a row means
            (a Reimbursable badge changes how you read the amount). */}
        <ul className="divide-y divide-edge">
          {(recent ?? []).map((t, i) => (
            <TxnListItem
              key={`${t.date}-${i}`}
              t={t}
              showAccount
              accounts={accounts ?? undefined}
              badge={<TagBadges tags={t.tags} policy={tagPolicy} />}
            />
          ))}
        </ul>
      </Card>
    </div>
  )
}
